import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import SplashNav from '../components/SplashNav';
import {
  Rocket, Search, FileText, Globe, Mail, BarChart3, Zap, Image, Rss,
  Wand2, BookOpen, MessageSquare, Link2, Calendar, Shield, Database,
  ArrowRight, Sparkles, Layers, Users, Target,
  Lock, RefreshCw, Send, Mic, Video, Tag, DollarSign,
  Wallet, Award, MousePointerClick, Settings, Play, Workflow,
  Building2, CreditCard, Monitor,
  Headphones, Cpu, Gauge, Palette,
  Megaphone, UserPlus, Star, Quote, Bot, Radar,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const SystemTour = lazy(() => import('../components/SystemTour'));

interface PublicTestimonial {
  id: string;
  author_name: string;
  author_role: string | null;
  author_company: string | null;
  rating: number;
  quote: string;
}

interface PublicOutput {
  id: string;
  title: string;
  description: string;
  slug: string;
  image_url: string;
  video_url: string | null;
  media_type: string;
  output_type: string;
  is_featured: boolean;
}

const OutputShowcaseSection: React.FC = () => {
  const [items, setItems] = useState<PublicOutput[]>([]);

  useEffect(() => {
    supabase
      .from('output_showcase_items')
      .select('id, title, description, slug, image_url, video_url, media_type, output_type, is_featured')
      .eq('is_published', true)
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(6)
      .then(({ data }) => setItems((data ?? []) as PublicOutput[]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5"><Sparkles className="h-3.5 w-3.5 text-cyan-600" /><span className="text-xs font-mono tracking-wider text-slate-500">OUTPUT SHOWCASE</span></div>
          <h2 className="text-2xl font-bold text-slate-800 md:text-3xl">See what the engine can create</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500">Explore real creative outputs, the prompts behind them, and the production cost behind each result.</p>
        </div>
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900">Create your own output <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map(item => {
          const isVideo = item.media_type === 'video' && item.video_url;
          return (
          <Link key={item.id} to={`/showcase/${item.slug}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
            <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">{isVideo ? <video src={item.video_url!} poster={item.image_url && item.image_url !== 'placeholder' ? item.image_url : undefined} muted className="h-full w-full object-cover" /> : <img src={item.image_url} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}{isVideo && <span className="absolute inset-0 flex items-center justify-center bg-slate-950/20"><span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg"><Play className="h-5 w-5 text-slate-800" /></span></span>}{item.is_featured && <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-amber-700"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> Featured</span>}</div>
            <div className="p-5"><p className="text-[10px] font-mono uppercase tracking-wider text-cyan-700">{item.output_type.replace(/_/g, ' ')}</p><h3 className="mt-1 font-semibold text-slate-800 group-hover:text-cyan-700">{item.title}</h3>{item.description && <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">{item.description}</p>}</div>
          </Link>
          );
        })}
      </div>
    </section>
  );
};

const TourSpotlight: React.FC = () => {
  const [tourOpen, setTourOpen] = useState(false);

  return (
    <section className="max-w-7xl mx-auto px-6 pt-8 pb-4">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-6 md:p-8 shadow-xl shadow-blue-900/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 right-10 h-48 w-48 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-blue-500/15 blur-3xl" />
        </div>
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-900/30">
              <Play className="h-5 w-5 text-white ml-0.5" />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 mb-1">First time here?</p>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-1">Take the platform tour</h2>
              <p className="text-sm text-slate-300 max-w-xl leading-relaxed">See how research, content creation, automation, publishing, and analytics work together in one command center.</p>
            </div>
          </div>
          <button
            onClick={() => setTourOpen(open => !open)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white text-blue-900 text-sm font-bold hover:bg-cyan-50 transition-colors shadow-lg flex-shrink-0"
          >
            <Play className="h-4 w-4" />
            {tourOpen ? 'Close Tour' : 'Start Tour'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {tourOpen && (
          <div className="relative mt-6 pt-6 border-t border-white/10">
            <Suspense fallback={<div className="py-10 text-center text-sm text-white/70">Loading the tour...</div>}>
              <SystemTour mode="embedded" onComplete={() => setTourOpen(false)} onSkip={() => setTourOpen(false)} />
            </Suspense>
          </div>
        )}
      </div>
    </section>
  );
};

const TestimonialsSection: React.FC<{ user: any }> = ({ user }) => {
  const [items, setItems] = useState<PublicTestimonial[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase
      .from('testimonials')
      .select('id, author_name, author_role, author_company, rating, quote')
      .eq('is_approved', true)
      .order('is_featured', { ascending: false })
      .order('display_order', { ascending: true })
      .order('submitted_at', { ascending: false })
      .limit(6)
      .then(({ data }) => {
        setItems((data ?? []) as PublicTestimonial[]);
        setLoaded(true);
      });
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
          <Star className="h-3.5 w-3.5 text-amber-500 fill-current" />
          <span className="text-xs font-mono text-slate-500 tracking-wider">CUSTOMER VOICES</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">What Our Users Say</h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto">Real reviews from real marketers using the platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(t => (
          <div key={t.id} className="glass-card p-6 hover:scale-[1.02] transition-transform duration-300 min-h-[220px]">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-amber-400 fill-current" />
                ))}
              </div>
              <Quote className="h-6 w-6 text-slate-200" />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-5">"{t.quote}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-rose-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {t.author_name?.[0]?.toUpperCase() ?? 'U'}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{t.author_name}</p>
                {(t.author_role || t.author_company) && (
                  <p className="text-xs text-slate-400 truncate">
                    {t.author_role}{t.author_role && t.author_company ? ' · ' : ''}{t.author_company}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center mt-8">
        <Link to={user ? '/testimonials' : '/login'} className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors">
          {user ? 'Share Your Own Review' : 'Sign In to Leave a Review'}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};

const Showcase: React.FC = () => {
  const { user } = useAuth();
  const { brandName, isWhiteLabeled } = useTenant();

  const modules = [
    { icon: Search, name: 'Research Hub', desc: 'Discover keywords, map competitors, analyze search volumes, and build research briefs with AI-powered semantic analysis.', color: 'primary', href: '/research' },
    { icon: Zap, name: 'Content Factory', desc: 'Generate e-books, email campaigns, ad copy, blog posts, social media, podcast scripts, and video scripts in parallel.', color: 'secondary', href: '/generate' },
    { icon: FileText, name: 'Content Library', desc: 'Browse, search, filter, and manage all generated content assets with compliance scoring and plagiarism detection.', color: 'accent', href: '/library' },
    { icon: Globe, name: 'Publishing Hub', desc: 'Deploy content to multiple targets — websites, CMS platforms, and custom endpoints — with one-click publishing.', color: 'success', href: '/publishing' },
    { icon: Mail, name: 'Email Marketing', desc: 'Auto-responder sequences, member management, product delivery, and behavioral targeting with smart segmentation.', color: 'primary', href: '/email' },
    { icon: Image, name: 'Image Generator', desc: 'Create visuals with AI — realistic, artistic, cartoon, minimalist styles. Ad banners, social media posts, background removal, upscaling, and color palette tools.', color: 'secondary', href: '/images' },
    { icon: BookOpen, name: 'Cover Page Generator', desc: 'Design professional e-book covers with multiple styles, color schemes, and live preview.', color: 'accent', href: '/covers' },
    { icon: Wand2, name: 'Prompt Creator', desc: 'Craft, save, and optimize AI prompts with templated workflows and variable injection for consistent output.', color: 'primary', href: '/prompt-creator' },
    { icon: Rss, name: 'RSS Creator', desc: 'Build and manage RSS content feeds for syndication and automated content distribution across channels.', color: 'secondary', href: '/rss' },
    { icon: Link2, name: 'Affiliate Portal', desc: 'Full affiliate program — apply, get a referral code, track referrals through the pipeline, request payouts via Stripe, and earn up to 30% recurring commission on every conversion.', color: 'success', href: '/affiliates' },
    { icon: Calendar, name: 'Social Media', desc: 'Plan and schedule posts across LinkedIn, Facebook, Instagram, and TikTok with a unified calendar view.', color: 'accent', href: '/scheduler' },
    { icon: Mic, name: 'Media Studio', desc: 'Generate AI podcasts with 11 natural voices via OpenAI TTS, create AI videos with xAI Grok, add branded watermarks, and manage all your media projects with built-in players.', color: 'accent', href: '/media-studio' },
    { icon: Target, name: 'Lead Capture Builder', desc: 'Build branded lead capture landing pages with custom forms, background images, banners, and full subscriber management with CSV import/export.', color: 'success', href: '/leads' },
    { icon: Bot, name: 'AI Marketing Manager', desc: 'Set a goal and the agent researches, generates multi-format content, creates images and videos, sends email campaigns, builds lead capture pages, checks compliance, proposes a publishing plan, and reports performance with actionable insights — all under your approval.', color: 'primary', href: '/marketing-manager' },
    { icon: Radar, name: 'Lead Generation', desc: 'Discover real prospects across social platforms, score fit with AI, review and approve leads, draft personalized outreach, and send only after human review.', color: 'success', href: '/lead-generation' },
    { icon: Monitor, name: 'Slideshow Builder', desc: 'Create interactive demo decks with images, icons, accent colors, auto-play, and per-slide image fit controls. Share with public links.', color: 'primary', href: '/slideshow' },
    { icon: BarChart3, name: 'Analytics', desc: 'Track page views, conversions, clicks, and engagement with real-time event tracking and visual dashboards.', color: 'primary', href: '/analytics' },
    { icon: Headphones, name: 'Live Support', desc: 'Real-time live chat support widget for your website visitors, plus an internal helpdesk with ticket tracking, canned responses, and agent routing.', color: 'accent', href: '/support' },
    { icon: UserPlus, name: 'Team Access', desc: 'Invite team members with role-based permissions — admins, editors, and viewers. Manage who can access what across your entire workspace.', color: 'success', href: '/team' },
    { icon: Megaphone, name: 'Campaigns Management', desc: 'Create, track, and optimize multi-channel marketing campaigns. Group content, emails, social posts, and ads under unified campaign goals with ROI tracking.', color: 'primary', href: '/campaigns' },
    { icon: Users, name: 'SaaS Pricing', desc: 'Subscription tiers from Free to Enterprise with billing management, affiliate program, and white-label branding on Agency and Enterprise.', color: 'secondary', href: '/pricing' },
  ];

  const colorMap: Record<string, string> = {
    primary: 'bg-blue-50 text-blue-600 border-blue-200',
    secondary: 'bg-violet-50 text-violet-600 border-violet-200',
    accent: 'bg-amber-50 text-amber-600 border-amber-200',
    success: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  };



  const capabilities = [
    { icon: Shield, title: 'Enterprise Security', desc: 'Your API keys are stored encrypted, and your content and subscriber data stay private to your account.' },
    { icon: Zap, title: 'Parallel Generation', desc: 'Generate 8+ content asset types simultaneously from a single research brief — e-books, emails, ads, metadata, and more.' },
    { icon: RefreshCw, title: 'Real-Time Sync', desc: 'Live dashboard updates as you work. Every new brief, asset, or post appears instantly across all connected devices.' },
    { icon: Layers, title: 'Multi-Channel Publishing', desc: 'Push content to websites, CMS platforms, social media, email sequences, and RSS feeds from a single publishing hub.' },
    { icon: Target, title: 'AI Compliance Scanning', desc: 'Built-in plagiarism detection and compliance scoring via Quetext integration ensures every asset is brand-safe.' },
    { icon: Database, title: 'Secure Cloud Storage', desc: 'Your work is saved safely in the cloud, so it is there when you sign in from any device.' },
  ];

  const pipeline = [
    { step: '01', icon: Search, label: 'Research', desc: 'Find keywords & competitors' },
    { step: '02', icon: Zap, label: 'Generate', desc: 'Create multi-format assets' },
    { step: '03', icon: Shield, label: 'Verify', desc: 'Compliance & plagiarism scan' },
    { step: '04', icon: Globe, label: 'Publish', desc: 'Deploy to all channels' },
    { step: '05', icon: BarChart3, label: 'Analyze', desc: 'Track & optimize performance' },
  ];

  const assetTypes = [
    { icon: FileText, label: 'E-books' },
    { icon: Mail, label: 'Email Campaigns' },
    { icon: Send, label: 'Ad Copy' },
    { icon: Tag, label: 'SEO Metadata' },
    { icon: FileText, label: 'Blog Posts' },
    { icon: MessageSquare, label: 'Social Posts' },
    { icon: Mic, label: 'Podcast Scripts' },
    { icon: Video, label: 'Video Scripts' },
  ];

  const affiliateFeatures = [
    { icon: MousePointerClick, label: 'Click Tracking', desc: 'Every referral click is tracked in real-time' },
    { icon: Users, label: 'Referral Pipeline', desc: 'Watch referrals move from click to signup to conversion' },
    { icon: DollarSign, label: 'Up to 30% Commission', desc: 'Earn on every paid subscription you refer' },
    { icon: Wallet, label: 'Flexible Payouts', desc: 'Stripe payouts with a A$50 minimum' },
    { icon: Award, label: 'Auto Referral Codes', desc: 'Unique codes generated on approval' },
  ];

  const payoutMethods = [
    { icon: CreditCard, label: 'Stripe', desc: 'Automatic transfers to your bank account' },
  ];

  const automationSteps = [
    { icon: Search, title: 'Trigger', desc: 'Research brief completed, schedule fires, or manual kick-off' },
    { icon: Zap, title: 'Generate', desc: 'AI creates content assets in parallel from your brief' },
    { icon: Shield, title: 'Verify', desc: 'Automatic compliance and plagiarism scanning' },
    { icon: Globe, title: 'Publish', desc: 'Deploy to all configured targets at once' },
    { icon: BarChart3, title: 'Analyze', desc: 'Track results and feed insights back into the pipeline' },
  ];

  const setupSteps = [
    { icon: Building2, label: 'Your Business', desc: 'Set your brand, industry, and target audience' },
    { icon: Users, label: 'Connect Channels', desc: 'Link social profiles and choose where to publish' },
    { icon: Rocket, label: 'Launch a Campaign', desc: 'Create your first brief, content set, and lead capture page' },
    { icon: Workflow, label: 'Automate & Grow', desc: 'Schedule research, compliance, publishing, and follow-up' },
  ];

  const stats = [
    { value: '21', label: 'Integrated Modules' },
    { value: '8', label: 'Content Asset Types' },
    { value: 'Up to 30%', label: 'Affiliate Commission' },
    { value: '11', label: 'AI Podcast Voices' },
    { value: '\u221E', label: 'Scalable Campaigns' },
  ];


  return (
    <div className="min-h-screen bg-slate-100">
      <SplashNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/15 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-8 animate-fade-in">
            <Sparkles className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">AI-POWERED MARKETING PLATFORM</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-800 tracking-tight mb-8 leading-[1.1] animate-slide-up">
            {isWhiteLabeled ? brandName : (
              <>
                Aurelian Marketing
                <span className="block gradient-text mt-2 pb-2">Engine</span>
              </>
            )}
          </h1>

          <p className="text-lg md:text-xl text-slate-500 max-w-3xl mx-auto mb-10 leading-relaxed animate-slide-up">
            Research, create, publish and nurture, all on autopilot. One AI command center replaces your entire marketing stack.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-scale-in">
            {user ? (
              <>
                <Link to="/dashboard" className="btn-primary px-8 py-3.5 text-sm flex items-center gap-2 group">
                  Launch Dashboard
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/setup" className="btn-ghost px-8 py-3.5 text-sm flex items-center gap-2">
                  <Rocket className="h-4 w-4" />
                  Setup Wizard
                </Link>
              </>
            ) : (
              <>
                <Link to="/login?mode=signup" className="btn-primary px-8 py-3.5 text-sm flex items-center gap-2 group">
                  Start Free
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/pricing" className="btn-ghost px-8 py-3.5 text-sm flex items-center gap-2">
                  <Rocket className="h-4 w-4" />
                  View Pricing
                </Link>
              </>
            )}
          </div>

          {!user && (
            <p className="mt-4 text-xs text-slate-400">Free plan · No credit card · No API keys needed</p>
          )}

          <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-6 max-w-4xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center group">
                <p className="text-3xl font-bold gradient-text group-hover:scale-110 transition-transform duration-300">{stat.value}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-mono mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Tour */}
      <TourSpotlight />

      {/* Customer Voices */}
      <TestimonialsSection user={user} />

      {/* Published Creative Outputs */}
      <OutputShowcaseSection />

      {/* AI Agent & Lead Generation */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4"><Bot className="h-3.5 w-3.5 text-cyan-600" /><span className="text-xs font-mono text-slate-500 tracking-wider">INTELLIGENT GROWTH SYSTEM</span></div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">From Marketing Goals to Qualified Conversations</h2>
          <p className="text-sm text-slate-500 max-w-2xl mx-auto">The AI Marketing Manager builds and executes a governed campaign workflow, while Lead Generation finds the people most likely to care.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="overflow-hidden rounded-2xl border border-cyan-200 bg-white shadow-sm">
            <div className="h-56 w-full bg-slate-950"><img src="/assets/images/AI_Marketing_Manager.png" alt="AI Marketing Manager robot assistant" loading="lazy" className="h-full w-full object-contain" /></div>
            <div className="p-6"><div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-xl bg-cyan-600 flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div><h3 className="text-lg font-bold text-slate-800">AI Marketing Manager</h3></div><p className="text-sm text-slate-500 leading-6">Give the agent a goal and it coordinates research, content, images, video, podcasts, email campaigns, lead capture pages, compliance checks, publishing, and performance analytics with actionable insights. Approval gates keep you in control before anything goes live.</p><div className="mt-4 flex flex-wrap gap-2">{['Goal-driven runs', 'Multi-format content', 'Email campaigns', 'Lead capture pages', 'Compliance first', 'Approval gates', 'Performance insights'].map(item => <span key={item} className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700">{item}</span>)}</div><Link to={user ? '/marketing-manager' : '/login'} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-700">Explore AI Marketing Manager <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
            <div className="h-56 w-full bg-slate-950"><img src="/assets/images/ai-image-d2cefaa3-bf69-485c-9e81-dc0de18456ed.png" alt="Lead Generation workspace with qualified prospect profiles" loading="lazy" className="h-full w-full object-contain" /></div>
            <div className="p-6"><div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center"><Radar className="h-5 w-5 text-white" /></div><h3 className="text-lg font-bold text-slate-800">Lead Generation</h3></div><p className="text-sm text-slate-500 leading-6">Search across public creator and business profiles, capture profile details, rank fit, and prepare personalized outreach. Every email remains a deliberate, human-approved action.</p><div className="mt-4 flex flex-wrap gap-2">{['Web discovery', 'AI scoring', 'Campaign filters', 'Personalized drafts', 'Human approval'].map(item => <span key={item} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">{item}</span>)}</div><Link to={user ? '/lead-generation' : '/login'} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">Explore Lead Generation <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </div>
      </section>

      {/* Setup Wizard Quick Access Banner */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="relative overflow-hidden glass-card p-6 md:p-8 border border-violet-200">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-violet-500/10 rounded-full blur-3xl" />
          </div>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
                <Rocket className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-slate-800">New to the platform?</h3>
                  <span className="px-2 py-0.5 bg-violet-100 text-violet-600 text-[10px] font-mono rounded uppercase tracking-wider">Quick Start</span>
                </div>
                <p className="text-sm text-slate-500 max-w-xl">Start with your business profile, connect the channels you want to use, launch a complete campaign, then let the platform handle research, creation, compliance, publishing, and follow-up in one guided flow.</p>
              </div>
            </div>
            <Link to={user ? '/setup' : '/login'} className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-all flex items-center gap-2 flex-shrink-0 text-sm shadow-md shadow-violet-500/20">
              <Play className="h-4 w-4" />
              {user ? 'Launch Setup Wizard' : 'Sign In & Set Up'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {setupSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-3 p-3 rounded-lg bg-white/60 border border-slate-200/60 hover:border-violet-200 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <step.icon className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono text-violet-500 uppercase tracking-wider">Step {i + 1}</p>
                  <p className="text-xs font-medium text-slate-700 truncate">{step.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section id="modules" className="max-w-7xl mx-auto px-6 py-16 scroll-mt-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Layers className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">FULL SUITE</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Platform Modules</h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">Twenty-one integrated tools working together as one seamless system</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map((m) => (
            <Link key={m.name} to={m.href} className={`glass-card glass-card-hover p-6 group border ${colorMap[m.color]}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colorMap[m.color]} group-hover:scale-110 transition-transform duration-300`}>
                  <m.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-slate-800 mb-1.5 group-hover:text-violet-600 transition-colors">{m.name}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{m.desc}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-violet-600 transition-colors flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Pipeline Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Workflow className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">CONTENT PIPELINE</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">The Marketing Pipeline</h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">From research to analysis — every step of your content marketing workflow in one place</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {pipeline.map((p, i) => (
            <div key={p.step} className="relative group">
              <div className="glass-card glass-card-hover p-5 text-center h-full group-hover:scale-105 transition-transform duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-violet-500/20">
                  <p.icon className="h-6 w-6 text-white" />
                </div>
                <p className="text-[10px] font-mono text-violet-600 mb-1">{p.step}</p>
                <h3 className="text-sm font-bold text-slate-800 mb-1">{p.label}</h3>
                <p className="text-[11px] text-slate-400">{p.desc}</p>
              </div>
              {i < pipeline.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-slate-200" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Automation Pipeline Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Workflow className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">AUTOMATION ENGINE</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Automate the Entire Pipeline</h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">
            Set up automation pipelines that run research, generation, compliance scanning, and publishing
            on a schedule — or trigger them manually with one click.
          </p>
        </div>

        <div className="glass-card p-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {automationSteps.map((step, i) => (
              <div key={step.title} className="relative group">
                <div className="text-center p-4 group-hover:scale-105 transition-transform duration-300">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-3 shadow-md shadow-violet-500/20">
                    <step.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-[10px] font-mono text-violet-500 mb-1">STAGE {String(i + 1).padStart(2, '0')}</p>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">{step.title}</h3>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{step.desc}</p>
                </div>
                {i < automationSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-slate-200" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Zap className="h-4 w-4 text-violet-500" />
              <span>Schedule pipelines to run automatically, or trigger them on demand</span>
            </div>
            <Link to={user ? '/automation' : '/login'} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2 shadow-md shadow-violet-500/20">
              <Workflow className="h-4 w-4" />
              {user ? 'Open Pipeline Engine' : 'Sign In to Automate'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Asset Types Banner */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="glass-card p-8 border border-violet-200">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 border border-violet-100 mb-3">
              <Cpu className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-mono text-violet-600 tracking-wider">PARALLEL AI GENERATION</span>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Generate 8 Content Types in Parallel</h2>
            <p className="text-sm text-slate-400">From a single research brief, the Content Factory produces all of these simultaneously</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {assetTypes.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-violet-50 hover:bg-violet-100 hover:scale-105 transition-all duration-300 cursor-default">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
                  <a.icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-[10px] text-slate-500 font-mono text-center">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Gauge className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">ENTERPRISE GRADE</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Enterprise Capabilities</h2>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">Built for scale, security, and performance from the ground up</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {capabilities.map((c) => (
            <div key={c.title} className="glass-card p-6 hover:scale-[1.02] transition-transform duration-300">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-4 shadow-md">
                <c.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-2">{c.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Affiliate Portal Highlight */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-8 md:p-12 shadow-xl shadow-emerald-500/20">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-emerald-300/20 rounded-full blur-2xl" />
          </div>

          <div className="relative">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm mb-6">
                <Link2 className="h-3.5 w-3.5 text-white" />
                <span className="text-xs font-mono text-white/80 tracking-wider">AFFILIATE PROGRAM</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">Earn Up to 30% Commission on Every Referral</h2>
              <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Join the affiliate program, get your unique referral code, and earn commission on every
                referral that converts to a paid subscription. Track clicks, signups, and conversions in
                real-time. Request payouts via Stripe with a A$50 minimum.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 max-w-3xl mx-auto">
              {[
                { key: 'starter', earnings: 'A$98', label: 'Starter: 10 referrals / mo at 20%' },
                { key: 'pro', earnings: 'A$372.50', label: 'Pro: 10 referrals / mo at 25%' },
                { key: 'agency', earnings: 'A$1,497', label: 'Agency: 10 referrals / mo at 30%' },
                { key: 'enterprise', earnings: 'A$2,997', label: 'Enterprise: 10 referrals / mo at 30%' },
              ].map((tier) => (
                <div key={tier.key} className="bg-white/15 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20 hover:bg-white/20 transition-colors">
                  <p className="text-2xl font-bold text-white">{tier.earnings}</p>
                  <p className="text-[10px] text-white/60 uppercase tracking-wider font-mono mt-1">{tier.label}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-white/60 -mt-6 mb-10">Examples show monthly commission from 10 paying referrals at each plan's first-year rate. Rates step down over time.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {affiliateFeatures.map((f) => (
                <div key={f.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                      <f.icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-white">{f.label}</h3>
                  </div>
                  <p className="text-xs text-white/70 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mb-10">
              <p className="text-center text-xs font-mono text-white/60 uppercase tracking-wider mb-4">Payout Method</p>
              <div className="grid grid-cols-1 gap-3 max-w-xs mx-auto">
                {payoutMethods.map((m) => (
                  <div key={m.label} className="flex items-center gap-3 p-4 bg-white/10 rounded-xl border border-white/15">
                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                      <m.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">{m.label}</p>
                      <p className="text-[10px] text-white/60">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <Link to={user ? '/affiliates' : '/login'} className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-emerald-600 rounded-lg font-bold text-sm hover:bg-white/90 transition-all shadow-lg">
                <Link2 className="h-4 w-4" />
                {user ? 'Open Affiliate Portal' : 'Sign In to Apply'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* White-Label Reseller Banner */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 p-8 md:p-10 shadow-xl shadow-amber-500/20">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-12 -left-12 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          </div>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <Palette className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">White-Label &amp; Reseller Platform</h3>
                <p className="text-sm text-white/80 max-w-lg leading-relaxed">Rebrand the entire platform with your own logo, colors, domain, and branded subdomain. Resell to your clients as your own product — available on Agency and Enterprise plans.</p>
              </div>
            </div>
            <Link to={user ? '/settings' : '/login'} className="px-6 py-3 bg-white text-amber-600 rounded-lg font-bold text-sm hover:bg-white/90 transition-all flex items-center gap-2 flex-shrink-0 shadow-lg">
              <Settings className="h-4 w-4" />
              {user ? 'Configure Branding' : 'Sign In'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative overflow-hidden glass-card p-10 md:p-16 text-center border border-violet-200">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-violet-500/15 rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-100 border border-violet-200 mb-6">
              <Lock className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-mono text-violet-600 tracking-wider">SECURE & READY</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Start Building Campaigns Today</h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto mb-8">
              Sign in to access all twenty-one modules, let the AI Marketing Manager run governed campaigns, discover prospects, and launch automation pipelines,
              create AI podcasts, and join the affiliate program to start earning.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={user ? '/dashboard' : '/login'} className="btn-primary px-8 py-3.5 text-sm inline-flex items-center gap-2 group">
                {user ? 'Enter Command Center' : 'Sign In to Get Started'}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/pricing" className="btn-ghost px-8 py-3.5 text-sm inline-flex items-center gap-2">
                View Pricing Plans
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with legal links */}
      {!user && <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-gradient-brand rounded-lg flex items-center justify-center">
                  <Rocket className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm font-bold text-slate-800 tracking-tight">{brandName}</span>
              </div>
              <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
                An AI-powered content marketing automation platform that unifies research, content generation,
                publishing, email marketing, media studio, analytics, and more into a single command center.
              </p>
              {!isWhiteLabeled && (
                <div className="flex flex-col gap-1.5 mt-4">
                  <a href="https://www.aurelianmarketingengine.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">www.aurelianmarketingengine.com</a>
                  <a href="https://www.aurelianwealthengine.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">www.aurelianwealthengine.com</a>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 font-mono">Platform</h4>
              <ul className="space-y-2">
                <li><Link to="/pricing" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Pricing</Link></li>
                <li><a href="#modules" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Engine Overview</a></li>
                <li><Link to="/splash/automation" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Automation</Link></li>
                <li><Link to="/splash/affiliate" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Affiliate Program</Link></li>
                <li><Link to="/roadmap" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Roadmap</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 font-mono">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/legal/terms" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Terms of Service</Link></li>
                <li><Link to="/legal/privacy" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/legal/cookies" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Cookie Policy</Link></li>
                <li><Link to="/legal/licensing" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">Licensing Agreement</Link></li>
                <li><Link to="/legal/compliance" className="text-xs text-slate-400 hover:text-primary-600 transition-colors">GDPR Compliance</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-300 font-mono">
              &copy; {new Date().getFullYear()} {brandName}. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-[10px] text-slate-300 font-mono">v2.0</span>
              {!isWhiteLabeled && <span className="text-[10px] text-slate-300 font-mono">Powered by AME</span>}
            </div>
          </div>
        </div>
      </footer>}

    </div>
  );
};

export default Showcase;
